import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { SERVICE_BY_SLUG, SERVICES } from "@/data/services";
import { Overline } from "@/components/brand/SectionDivider";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { BRAND } from "@/data/brand";
import EvaluationForm from "@/components/forms/EvaluationForm";
import { ConcentricMonogram } from "@/components/brand/ConcentricMonogram";
import { AmbientLight } from "@/components/brand/AmbientLight";
import useDocumentTitle from "@/lib/useDocumentTitle";

const TYPICAL_STEPS = [
  "Policy review and engagement",
  "On-site inspection and evidence collection",
  "Scope, estimate, and code-compliance documentation",
  "Carrier presentation, negotiation, and supplements",
  "Settlement and recovery",
];

export default function ClaimService() {
  const { slug } = useParams();
  const service = SERVICE_BY_SLUG[slug];
  useDocumentTitle(service ? `${service.title} | AG Public Adjusting` : "Claim Services | AG Public Adjusting");
  if (!service) return <Navigate to="/claim-services" replace />;

  const related = SERVICES.filter((s) => s.category === service.category && s.slug !== slug).slice(0, 3);

  return (
    <main data-testid={`service-page-${slug}`} className="pt-32 bg-midnight-gradient">
      {/* Hero */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <AmbientLight size={1000} color="blue" intensity={0.5} style={{ top: "-10%", right: "0%" }} />
        <div className="hidden lg:block absolute right-12 top-1/2 -translate-y-1/2 opacity-50 pointer-events-none" aria-hidden>
          <ConcentricMonogram size={420} variant="ghost" glowColor="blue" />
        </div>

        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
          <Link to="/claim-services" className="overline text-gold/70 hover:text-gold inline-flex items-center gap-2 mb-8">
            ← Claim Services
          </Link>
          <h1 className="font-display-tight text-white text-[clamp(2.8rem,6vw,6rem)] leading-[0.95] tracking-[-0.045em] max-w-4xl">
            {service.title}
          </h1>
          <p className="mt-10 text-ag-text-secondary text-lg lg:text-xl leading-[1.65] font-light max-w-2xl">{service.short}</p>
        </div>
      </section>

      {/* The challenge */}
      <section className="py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Overline className="mb-8">The Challenge</Overline>
            <h2 className="font-display-tight text-white text-[clamp(2rem,3.5vw,3rem)] leading-[1.05] tracking-[-0.035em]">
              Why this claim type <span className="gold-metallic font-display font-light">requires representation.</span>
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-ag-text-secondary text-lg leading-[1.65] font-light">{service.challenge}</p>
          </div>
        </div>
      </section>

      {/* How the firm handles it */}
      <section className="relative py-24 lg:py-32 border-t border-white/5 bg-midnight-deeper overflow-hidden">
        <AmbientLight size={700} color="gold" intensity={0.25} style={{ top: "50%", left: "30%" }} />
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Overline className="mb-8">How the Firm Handles It</Overline>
            <h2 className="font-display-tight text-white text-[clamp(2rem,3.5vw,3rem)] leading-[1.05] tracking-[-0.035em]">
              The AG Method,
              <br />
              <span className="gold-metallic font-display font-light">applied here.</span>
            </h2>
            <Link to="/process" className="mt-10 inline-flex items-center gap-2 font-mono text-xs tracking-[0.18em] uppercase text-gold hover:text-gold-light">
              The AG Method <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="lg:col-span-7">
            <ol className="space-y-6">
              {TYPICAL_STEPS.map((step, i) => (
                <li key={step} className="flex gap-6 items-start glass p-5">
                  <span className="font-display gold-metallic text-2xl tracking-tight w-10 font-light">0{i + 1}</span>
                  <span className="text-white text-base leading-[1.6] font-light pt-1">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* CTA + Form */}
      <section className="relative py-24 lg:py-32 border-t border-white/5 overflow-hidden">
        <AmbientLight size={900} color="blue" intensity={0.3} style={{ bottom: "-10%", right: "0%" }} />
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Overline className="mb-8">Begin Evaluation</Overline>
            <h2 className="font-display-tight text-white text-[clamp(2rem,4vw,3.5rem)] leading-[1.05] tracking-[-0.04em]">
              Have a <span className="gold-metallic font-display font-light">{service.title.toLowerCase().replace(" claims", "")}</span> claim?
            </h2>
            <p className="mt-8 text-ag-text-secondary leading-[1.65] font-light">
              The firm provides a no-obligation review. Contingency-based engagement.
            </p>
            <ul className="mt-10 space-y-4">
              {["Confidential review", "One business day response", "No recovery, no fee"].map((b) => (
                <li key={b} className="flex items-center gap-3 text-sm text-white font-light">
                  <Check className="w-4 h-4 text-gold" />
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <EvaluationForm />
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-24 lg:py-32 border-t border-white/5 bg-midnight-deeper">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <Overline className="mb-10">Related Claim Services</Overline>
            <div className="grid md:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/claim-services/${r.slug}`}
                  data-testid={`related-service-${r.slug}`}
                  className="glass block p-8 group hover:border-gold/30 transition-all duration-500"
                >
                  <h3 className="font-display text-xl text-white group-hover:gold-metallic group-hover:text-transparent transition-colors mb-3 font-light tracking-[-0.02em]">
                    {r.title}
                  </h3>
                  <p className="text-sm text-ag-text-secondary font-light line-clamp-2 leading-[1.6]">{r.short}</p>
                  <ArrowRight className="w-4 h-4 text-gold mt-6" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
